import { TableSortBy } from '@acceso/design-system';
import { debounce } from 'lodash';
import { useMemo, useState } from 'react';

export interface PaginationFilters {
  onChangePage: (page: number) => void;
  onChangeRows: (rows: number) => void;
  onChangeQuery: (query: string) => void;
  onSortBy: (sort: TableSortBy) => void;
  reset: () => void;
}

export interface PaginationValues {
  query: string;
  offset: number;
  rowsPerPage: number;
  currentPage: number;
  sortBy: TableSortBy | undefined;
}

export interface PaginationOptions {
  values: PaginationValues;
  filters: PaginationFilters;
}

interface UsePaginationProps {
  defaultSortBy?: TableSortBy;
}

const usePagination = ({
  defaultSortBy = { id: null, order: null },
}: UsePaginationProps = {}): PaginationOptions => {
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const [sortBy, setSetsortBy] = useState<TableSortBy>(defaultSortBy);

  const onChangePage = (page: number) => setCurrentPage(page);
  const onChangeRows = (rows: number) => setrowsPerPage(rows);
  const onChangeQuery = debounce((query: string) => {
    setQuery(query);
    setCurrentPage(1);
  }, 400);
  const onSortBy = (sort: TableSortBy) => setSetsortBy(sort);
  // Synchronous on purpose: onChangeQuery is debounced, so clearing the term
  // through it would leave a window in which the stale one is still published.
  const reset = () => {
    onChangeQuery.cancel();
    setQuery('');
    setCurrentPage(1);
    setrowsPerPage(10);
    setSetsortBy(defaultSortBy);
  };

  const offset = useMemo(() => (currentPage - 1) * rowsPerPage, [currentPage, rowsPerPage]);

  return {
    values: {
      query,
      offset,
      currentPage,
      rowsPerPage,
      sortBy,
    },
    filters: {
      onChangePage,
      onChangeRows,
      onChangeQuery,
      onSortBy,
      reset,
    },
  };
};

export { usePagination };
